<script lang="ts">
  // Locks sit per link, never on the group (Storytelling's Improv is open to
  // guests). Links are never gated; each page owns its lock and CTA.
  import { tick } from 'svelte';
  import { page } from '$app/stores';
  import { resolveHref } from '$lib/utils/routing/resolveHref';
  import { pageNameFromTitle } from '$lib/utils/routing/navRoutes';
  import type { Pathname } from '$app/types';
  import { auth, isPremium } from '$lib/stores/auth';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';
  import AccountMenu from '$lib/ui/nav/AccountMenu.svelte';
  import { Lock } from '$lib/ui/icons';

  // `requires` mirrors each page's own gate (feature-access-model.md §6).
  type NavLink = { href: Pathname; label: string; requires?: 'account' | 'premium' };

  const playful: NavLink[] = [
    { href: '/', label: UiKeys.Nav.Instrument },
    { href: '/body-language', label: UiKeys.Nav.BodyLanguage },
    { href: '/archetypes', label: UiKeys.Nav.Archetype },
    { href: '/word-play', label: UiKeys.Nav.Riff },
  ];

  const masteryLinks: NavLink[] = [
    { href: '/storytelling', label: UiKeys.Nav.Storytelling },
    { href: '/rehearsal', label: UiKeys.Nav.RecordReview, requires: 'premium' },
    { href: '/active-listening', label: UiKeys.Nav.ActiveListening, requires: 'account' },
    { href: '/progress', label: UiKeys.Nav.Progress, requires: 'premium' },
  ];

  const library: NavLink = { href: '/library', label: UiKeys.Nav.Library };

  function isActive(href: string, pathname: string): boolean {
    return href === '/' ? pathname === '/' : pathname.startsWith(href);
  }

  let open = $state(false);
  let barEl: HTMLDivElement | null = $state(null);
  let panelEl: HTMLDivElement | null = $state(null);
  let toggleEl: HTMLButtonElement | null = $state(null);

  // The panel stays in the DOM for its transition; `inert`, not the clip, keeps
  // closed links out of the tab order.
  async function toggleOpen(): Promise<void> {
    open = !open;
    if (!open) return;
    await tick();
    panelEl?.querySelector('a')?.focus({ preventScroll: true });
  }

  function onBarKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !open) return;
    open = false;
    toggleEl?.focus();
  }

  // A disclosure, not a dialog: tabbing out closes it, so focus never lands
  // under the overlay.
  function onBarFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget;
    if (!open || !(next instanceof Node) || barEl?.contains(next)) return;
    open = false;
  }

  // No lock while `loading`, so a signed-in visitor never sees one flicker.
  function lockLabel(link: NavLink): string | null {
    if (link.requires === 'premium' && $auth.status !== 'loading' && !$isPremium) return UiKeys.Nav.PremiumLockTooltip;
    if (link.requires === 'account' && $auth.status === 'guest') return UiKeys.Nav.MasteryLockTooltip;
    return null;
  }

  $effect(() => {
    const _ = $page.url.pathname;
    open = false;
  });

  let hasHeading = $state(false);
  let headingOnScreen = $state(true);

  // typeScale.test.ts asserts every practice page renders `h1.page-title`.
  $effect(() => {
    const _ = $page.url.pathname;
    const heading = document.querySelector('h1.page-title');
    hasHeading = heading !== null;
    headingOnScreen = true;
    if (!heading || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      headingOnScreen = entry.isIntersecting;
    });
    observer.observe(heading);
    return () => observer.disconnect();
  });

  const titleCollapsed = $derived(hasHeading && !headingOnScreen);

  // Off the practice list the page names itself once, in its <svelte:head> title.
  let documentTitle = $state('');
  $effect(() => {
    documentTitle = document.title;
    const observer = new MutationObserver(() => (documentTitle = document.title));
    observer.observe(document.head, { subtree: true, childList: true, characterData: true });
    return () => observer.disconnect();
  });

  const activeLink = $derived(
    [...playful, ...masteryLinks, library].find((link) => isActive(link.href, $page.url.pathname)),
  );
  const pageName = $derived(activeLink ? t(activeLink.label) : pageNameFromTitle(documentTitle));

  // The full nav stays in flow; once it scrolls past, it turns into a fixed app
  // bar and its slot keeps the measured height so the page never moves.
  let slotEl: HTMLDivElement | null = $state(null);
  let navEl: HTMLElement | null = $state(null);
  let compact = $state(false);
  let reservedHeight = $state(0);

  $effect(() => {
    if (!slotEl || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && !compact) reservedHeight = navEl?.getBoundingClientRect().height ?? 0;
      compact = !entry.isIntersecting;
    });
    observer.observe(slotEl);
    return () => observer.disconnect();
  });
</script>

<div class="nav-slot" bind:this={slotEl} style:height={compact ? `${reservedHeight}px` : undefined}>
  <nav class="practice-nav no-print" class:compact bind:this={navEl} aria-label={t(UiKeys.Nav.PracticeNavAria)}>
    <div class="nav-inner">
      <span class="appbar-title" data-testid="appbar-title" aria-hidden="true">{pageName}</span>
      <div class="nav-row">
        <div class="group">
          <span class="group-label">{t(UiKeys.Nav.Playground)}</span>
          {#each playful as link (link.href)}
            <a
              href={resolveHref(link.href)}
              class:active={isActive(link.href, $page.url.pathname)}
              aria-current={isActive(link.href, $page.url.pathname) ? 'page' : undefined}
              >{t(link.label)}</a
            >
          {/each}
        </div>

        <div class="row-end account-group" aria-label={t(UiKeys.Nav.Account)}>
          <AccountMenu variant="inline" />
        </div>
      </div>

      <div class="nav-row">
        <div class="group">
          <span class="group-label">{t(UiKeys.Nav.Mastery)}</span>
          {#each masteryLinks as link (link.href)}
            {@const lock = lockLabel(link)}
            <a
              href={resolveHref(link.href)}
              class:active={isActive(link.href, $page.url.pathname)}
              aria-current={isActive(link.href, $page.url.pathname) ? 'page' : undefined}
              >{t(link.label)}{#if lock}<span
                  class="nav-lock"
                  role="img"
                  aria-label={t(lock)}
                  title={t(lock)}
                  ><Lock size={13} strokeWidth={2} aria-hidden="true" focusable="false" /></span
                >{/if}</a
            >
          {/each}
        </div>

        <div class="row-end">
          <a
            class="recent-link"
            href={resolveHref(library.href)}
            class:active={isActive(library.href, $page.url.pathname)}
            aria-current={isActive(library.href, $page.url.pathname) ? 'page' : undefined}
            >{t(library.label)}</a
          >
        </div>
      </div>
    </div>
  </nav>
</div>

<!-- The bar only delegates its own controls' key and focus events. -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="mobile-bar no-print" bind:this={barEl} onkeydown={onBarKeydown} onfocusout={onBarFocusOut}>
  <!-- aria-hidden: the page's h1 still owns its accessible name. -->
  <div class="bar-title" class:collapsed={titleCollapsed}>
    <a class="wordmark" href={resolveHref('/')}>{t(UiKeys.Nav.Wordmark)}</a>
    <span class="bar-page-title" data-testid="nav-page-title" aria-hidden="true">{pageName}</span>
  </div>

  <button
    class="hamburger"
    class:open
    bind:this={toggleEl}
    onclick={toggleOpen}
    aria-label={open ? t(UiKeys.Nav.CloseNav) : t(UiKeys.Nav.OpenNav)}
    aria-expanded={open}
    aria-controls="mobile-nav"
  >
    <span></span>
    <span></span>
    <span></span>
  </button>

  <div id="mobile-nav" class="mobile-panel" class:open inert={!open} bind:this={panelEl}>
    <div class="mobile-inner">
      <div class="mobile-group">
        <span class="group-label">{t(UiKeys.Nav.Playground)}</span>
        {#each playful as link (link.href)}
          <a
            href={resolveHref(link.href)}
            class:active={isActive(link.href, $page.url.pathname)}
            aria-current={isActive(link.href, $page.url.pathname) ? 'page' : undefined}
            >{t(link.label)}</a
          >
        {/each}
      </div>
      <div class="mobile-group">
        <span class="group-label">{t(UiKeys.Nav.Mastery)}</span>
        {#each masteryLinks as link (link.href)}
          {@const lock = lockLabel(link)}
          <a
            href={resolveHref(link.href)}
            class:active={isActive(link.href, $page.url.pathname)}
            aria-current={isActive(link.href, $page.url.pathname) ? 'page' : undefined}
            >{t(link.label)}{#if lock}<span
                class="nav-lock"
                role="img"
                aria-label={t(lock)}
                title={t(lock)}
                ><Lock size={13} strokeWidth={2} aria-hidden="true" focusable="false" /></span
              >{/if}</a
          >
        {/each}
      </div>

      <div class="mobile-group mobile-account" aria-label={t(UiKeys.Nav.Account)}>
        <span class="group-label">{t(UiKeys.Nav.Account)}</span>
        <AccountMenu variant="block" />
      </div>

      <div class="mobile-group">
        <a
          href={resolveHref(library.href)}
          class:active={isActive(library.href, $page.url.pathname)}
          aria-current={isActive(library.href, $page.url.pathname) ? 'page' : undefined}
          >{t(library.label)}</a
        >
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  @use '../../styles/tokens' as *;

  .practice-nav {
    display: block;
    background: var(--paper);
    border-bottom: 1px solid var(--line);
    font-family: var(--font-ui);
  }

  /* One grid for both rows keeps columns aligned in every language. */
  .nav-inner {
    display: grid;
    grid-template-columns: 120px repeat(3, minmax(140px, auto)) auto minmax(0, 1fr);
    align-items: center;
    gap: 0.5rem;
    max-width: calc(var(--content-max) + 2 * var(--page-gutter));
    margin: 0 auto;
    padding: 0.75rem var(--page-gutter);
  }

  .nav-row,
  .group {
    display: contents;
  }

  .appbar-title {
    display: none;
  }

  /* Same layer as the mobile bar: above page content, below modals. */
  .practice-nav.compact {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 45;
    box-shadow: 0 2px 8px rgb(0 0 0 / 0.08);
    animation: appbar-in 0.2s ease-out;

    .nav-inner {
      display: flex;
      padding-block: 0.4rem;
    }

    .group {
      display: none;
    }

    .appbar-title {
      display: block;
      flex: 1 1 auto;
      min-width: 0;
      font-size: var(--text-body);
      font-weight: 700;
      color: var(--ink);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .account-group {
      order: 1;
    }
  }

  @keyframes appbar-in {
    from {
      transform: translateY(-100%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .practice-nav.compact {
      animation: none;
    }
  }

  /* The account cluster, not the links, absorbs a long greeting. */
  .row-end {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  .group-label {
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--ink-mute);
  }

  .nav-lock {
    display: inline-flex;
    margin-left: 0.25rem;
    color: currentcolor;
    opacity: 0.55;
    line-height: 1;
    cursor: help;
    vertical-align: middle;
  }

  .nav-lock:hover {
    opacity: 0.85;
  }

  a {
    white-space: nowrap;
    color: var(--ink-soft);
    text-decoration: none;
    font-size: var(--text-body);
    font-weight: 400;
    padding: 0.35rem 0.6rem;
    border-radius: 6px;
  }

  a:hover {
    background: color-mix(in srgb, var(--accent) 8%, transparent);
    color: var(--ink);
  }

  a.active {
    color: color-mix(in srgb, var(--accent-text) 85%, var(--ink));
    background: color-mix(in srgb, var(--accent) 12%, transparent);
    font-weight: 700;
    box-shadow: inset 0 -2px 0 var(--accent);
  }

  /* Above page content (≤20), below modals (≥50). */
  .mobile-bar {
    display: none;
    position: sticky;
    top: 0;
    z-index: 45;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    min-height: var(--mobile-bar-h);
    padding: 0 1rem;
    background: var(--paper);
    border-bottom: 1px solid var(--line);
    font-family: var(--font-ui);
  }

  /* One cell for both texts, so the hamburger never shifts on scroll. */
  .bar-title {
    display: grid;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
  }

  .bar-title > * {
    grid-area: 1 / 1;
    transition: opacity 0.2s ease, visibility 0.2s ease;
  }

  .bar-page-title {
    justify-self: start;
    max-width: 100%;
    font-size: var(--text-body);
    font-weight: 700;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 0;
    visibility: hidden;
  }

  .bar-title.collapsed .wordmark {
    opacity: 0;
    visibility: hidden;
  }

  .bar-title.collapsed .bar-page-title {
    opacity: 1;
    visibility: visible;
  }

  @media (prefers-reduced-motion: reduce) {
    .bar-title > * {
      transition: none;
    }
  }

  /* Brand token, not `--accent-text`: the practice shell reslots the accent. */
  .wordmark {
    justify-self: start;
    min-width: 0;
    padding: 0.25rem 0.4rem;
    font-family: var(--font-hand);
    font-size: var(--text-brand);
    line-height: 1;
    color: var(--color-brand-text);
    white-space: nowrap;
  }

  .hamburger {
    display: none;
    flex: 0 0 auto;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    width: 44px; /* the tap-target floor */
    height: 44px;
    padding: 0;
    background: var(--ink);
    border: none;
    border-radius: 22px;
    cursor: pointer;
  }

  .hamburger span {
    display: block;
    width: 22px;
    height: 2px;
    background: var(--paper);
    border-radius: 2px;
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .hamburger.open span:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
  }

  .hamburger.open span:nth-child(2) {
    opacity: 0;
  }

  .hamburger.open span:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
  }

  .mobile-panel {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--paper);
    box-shadow: 0 4px 12px rgb(0 0 0 / 0.12);
    overflow: hidden;
    max-height: 0;
    opacity: 0;
    transition:
      max-height 0.3s ease,
      opacity 0.2s ease;
  }

  .mobile-panel.open {
    max-height: calc(100vh - var(--mobile-bar-h)); /* fallback */
    max-height: calc(100dvh - var(--mobile-bar-h));
    overflow-y: auto;
    overscroll-behavior: contain;
    opacity: 1;
  }

  .mobile-inner {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.25rem 1rem;
    font-family: var(--font-ui);
  }

  .mobile-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .mobile-group .group-label {
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--ink-mute);
    margin-bottom: 0.25rem;
  }

  .mobile-group a {
    display: block;
    white-space: nowrap;
    color: var(--ink-soft);
    text-decoration: none;
    font-size: var(--text-body);
    font-weight: 400;
    padding: 0.5rem 0.6rem;
    border-radius: 6px;
  }

  .mobile-group a:hover {
    background: color-mix(in srgb, var(--accent) 8%, transparent);
    color: var(--ink);
  }

  .mobile-group a.active {
    color: color-mix(in srgb, var(--accent-text) 85%, var(--ink));
    background: color-mix(in srgb, var(--accent) 12%, transparent);
    font-weight: 700;
    box-shadow: inset 3px 0 0 var(--accent);
  }

  /* Vietnamese "Đăng nhập" still wraps below ~1066px. */
  @media (max-width: $nav-toggle-bp) {
    .practice-nav {
      display: none;
    }

    .mobile-bar {
      display: flex;
    }

    .hamburger {
      display: flex;
    }

    .mobile-panel {
      display: block;
    }
  }
</style>
